
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import React from 'react';
import { searchToParamValuesMap, VerticalFlexContainer } from '../../../data-catalog-shared';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { ErrorToasts } from '../error/components/ErrorToasts';
import { RegisterErrorStoreInHost, RegisterErrorStoreInHostProps } from '../error/components/RegisterErrorStoreInHost';
import { CatalogBrowserCountsQueryWrapper } from './CatalogBrowserCountsQueryWrapper';
import { CatalogBrowserHeader, CatalogBrowserSubplace } from './CatalogBrowserHeader';
import { WebClientApiContentTypeCountsResponse } from './CatalogBrowserTypes';

const hasActiveWarningParamKey = 'hasActiveWarning';
const isCertifiedParamKey = 'isCertified';

export type CatalogBrowserPlacePageLayoutProps =
  RegisterErrorStoreInHostProps &
  {
    readonly selectedSubplace: CatalogBrowserSubplace;
    readonly locationSearch: string;
    readonly renderExplorerComponent: (routeLocationSearch: string) => React.ReactNode;
    readonly webClientResult?: WebClientApiContentTypeCountsResponse;
  };

export class CatalogBrowserPlacePageLayout extends React.Component<CatalogBrowserPlacePageLayoutProps> {
  public override render(): JSX.Element {
    const searchParams = textSearchFromLocationSearch(this.props.locationSearch);
    return (
      <ErrorListProvider key={this.props.selectedSubplace}>
        <RegisterErrorStoreInHost
          registerErrorStore={this.props.registerErrorStore}
          unregisterErrorStore={this.props.unregisterErrorStore}
        />
        <ErrorToasts />
        <CatalogBrowserCountsQueryWrapper
          textSearch={searchParams.search}
          hasActiveWarning={searchParams.hasActiveWarning}
          isCertified={searchParams.isCertified}
          webClientResult={this.props.webClientResult}
        >
          {({ databasesCount, tablesCount }) =>
            <VerticalFlexContainer fillFlexParent={true}>
              <CatalogBrowserHeader
                databasesCount={databasesCount}
                tablesCount={tablesCount}
                selectedSubplace={this.props.selectedSubplace}
              />
              {this.props.renderExplorerComponent(this.props.locationSearch)}
            </VerticalFlexContainer>
          }
        </CatalogBrowserCountsQueryWrapper>
      </ErrorListProvider>
    );
  }
}

type CatalogBrowserFilterParams = {
  readonly search: string | null;
  readonly hasActiveWarning: boolean | null;
  readonly isCertified: boolean | null;
};

function textSearchFromLocationSearch(locationSearch: string): CatalogBrowserFilterParams {
  const paramValuesMap = searchToParamValuesMap(locationSearch);

  return {
    search: getFirstParamValueOrNull(paramValuesMap[CatalogUrlParamKeys.search]),
    hasActiveWarning: toBooleanOrNull(getFirstParamValueOrNull(paramValuesMap[hasActiveWarningParamKey])),
    isCertified: toTrueOrNull(getFirstParamValueOrNull(paramValuesMap[isCertifiedParamKey])),
  };
}

export function getFirstParamValueOrNull(values: readonly string[] | undefined): string | null {
  return values && values.length > 0 ? values[0] : null;
}

// Used for binary filters that can only be "off" or "on"
export function toTrueOrNull(value: string | null): boolean | null {
  if (value == null) {
    return null;
  } else {
    const lowercaseValue = value.toLowerCase();
    return lowercaseValue === 'true' ? true : null;
  }
}

// Used for ternary filters that can be "off", "on - true", or "on - false"
export function toBooleanOrNull(value: string | null): boolean | null {
  if (value == null) {
    return null;
  } else {
    const lowercaseValue = value.toLowerCase();
    return lowercaseValue === 'true' ? true : lowercaseValue === 'false' ? false : null;
  }
}
